<template>
	<div class="rb-container">
		<h2 class="rb-header" v-bind="[
			{class: {loading}}
		]">
			{{ title }}
		</h2>

		<div v-if="loading">
			<div class="loader-container" v-bind="ADAattr('progress', 'Loading items')">
				<div class="loader">
					<svg class="circular" viewBox="25 25 50 50">
						<circle class="path" cx="50" cy="50" r="20" fill="none" stroke-width="2" stroke-miterlimit="10"/>
					</svg>
				</div>
			</div>
		</div>

		<div v-else class="rbc-other-materials">
			<div class="rbc-section">
				<div class="rbc-section-content" role="list">
					<template v-if="more && !loading">
						<v-btn
							class="more ml-0"
							color="grey lighten-1"
							@click="offset -= limit"
							v-bind="ADAattr('label', 'Load more items')"
							outline
							small
							fab
						>
							<v-icon size="24" color="grey darken-2">chevron_left</v-icon>
						</v-btn>
					</template>
					<div class="rbc-item-container" v-for="item in items">
						<div
							:class="['rbc-item-container-body', {'ada-hover': item.ada_hover}]"
							@focusin="$set(item, 'ada_hover', true)"
							@blur.capture="$set(item, 'ada_hover', false)"
						>
							<div class="rbc-item" v-bind="ADAattr('hidden')">
								<div class="rbc-item-internal">
									<div class="rbc-item-cover">
										<div v-bind:style="{ background: `url(${item.cover || '/img/no_image_medium.gif'}) 50% 50% no-repeat`, 'background-size': 'contain' }"></div>
									</div>

									<div class="rbc-item-info" v-if="item['type'] === 'required'">
										<div class="rbc-item-type rbc-item-type-required">
											<span class="rbc-item-type-icon">new_releases</span>
											<span>Required</span>
										</div>
									</div>

									<div class="rbc-item-details">
										<strong>{{ item.title }}</strong>
										<span>{{ item.author }}</span>
									</div>
								</div>
							</div>
							<div class="rbc-item-hover">
								<div
									class="rbc-item-internal" tabindex="0" role="listitem"
									v-bind="[
										ADAattr('label', `${ADAitemLabel(item.title, item.isbn, item['type'] === 'required')}`)
									]"
								>
									<div class="rbc-item-main" v-bind="ADAattr('hidden')">
										<div class="rbc-item-cover">
											<div v-bind:style="{ background: `url(${item.cover || '/img/no_image_medium.gif'}) 50% 50% no-repeat`, 'background-size': 'contain' }"></div>
										</div>

										<div class="rbc-tem-info-aside">
											<div class="rbc-item-info" v-if="item['type'] === 'required'">
												<div class="rbc-item-type rbc-item-type-required">
													<span class="rbc-item-type-icon">new_releases</span>
													<span>Required</span>
												</div>
											</div>

											<div class="rbc-item-details">
												<strong>{{ item.title }}</strong>
												<span>{{ item.author }}</span>
											</div>
										</div>
									</div>
								</div>
								<div class="rbc-item-internal-additional">
									<div class="rbc-item-add-line" v-for="product in item['all_prices']">
										<div class="rbc-item-add-price" v-bind="ADAattr('hidden')">
											<span>{{ product['type'] }}:</span>
											<strong>${{ product['product_price'] }}</strong>
											<template v-if="product['disclaimer']">
												<div class="product-disclaimer-container">
													<span aria-hidden="true" class="mdi mdi-information-outline"></span>
													<span class="product-disclaimer-text" v-html="product['disclaimer']"></span>
												</div>
											</template>
										</div>
										<div class="rbc-item-add-btn">
											<a :href="product | addToCartLink"
													@click="doTracking(product['product_id'], product['type'])"
													:data-gaaction="item.ga_action"
													data-ecommerce-action="add_to_cart"
													:data-ecommerce-product="JSON.stringify({'item_id' : item.isbn, 'quantity' : '1', 'item_category' : 'book', 'item_variant' : product['type'], 'item_name' : item.title})"
													v-bind="ADAattr('label', ADAaddItemToCartLabel(product['type'], product['product_price']))"
											>Add to Cart</a>
										</div>
									</div>
									<div class="rbc-item-add-line" v-if="item['link']">
										<div class="rbc-item-add-btn">
											<a :href="item['link']" :data-gaaction="item.ga_action">
												{{ item['link_title'] }}
											</a>

										</div>
									</div>

								</div>
							</div>
						</div>
					</div>
					<template v-if="more && !loading">
						<v-btn
							class="more mr-0"
							color="grey lighten-1"
							@click="offset += limit"
							v-bind="ADAattr('label', 'Load more items')"
							outline
							small
							fab
						>
							<v-icon size="24" color="grey darken-2">chevron_right</v-icon>
						</v-btn>
					</template>
				</div>
			</div>
		</div>

	</div>
</template>

<script>
	export default {
		name: "RequiredBooksBlock",

		props: {
			items: {
				type: Array,
				default: [],
			},

			limit: {
				type: Number,
				default: 6,
			},

			title: {
				type: String,
				default: '',
			},

			loading: {
				type: Boolean,
				default: false,
			},
		},

		data() {
			return {
				offset: 0,
			};
		},

		computed: {
			more() {
				return 'load-more' in this.$listeners;
			},
		},

		watch: {
			offset(value) {
				this.$emit('load-more', value);
			},
		},

		filters: {
			addToCartLink(product) {
				const order_type = product['type'] === 'rental' ? `${product['type']}${product['rental_term_id']}` : 'purchase';
				const partner = product['is_alibris'] ? '&partner=alibris' : '';
				return `/sc/index.php?productId=${product['product_id']}&action=add&quantity=1&order_type=${order_type}${partner}`;
			}
		},

		mounted() {

		},

		methods: {
			doTracking(pid, ptype) {
				if (typeof sendFBPixelAction === 'function') {sendFBPixelAction('AddToCart', {content_ids: pid, content_type: ptype})}
			},
		},
	}
</script>

<style scoped lang="less">
	@text_highlight_color: #2170f2;

	.rb-container {
		margin: 0 0 30px;

		.rb-header {
			border-top: 1px solid #E7E7E7;
			font-size: 18px;
			font-weight: bold;
			padding: 30px 0 10px;
			color: #3d3d3d !important;
			&.loading {
				color: #ddd;
			}
		}

		.rbc-other-materials {
			display: flex;
			flex-grow: 1;
			flex-wrap: wrap;

			.rbc-section-title {
				display: none;

				.rbc-section-title-content {
					width: 90%;
					margin: 0 5% 20px;
					text-align: center;
					border-bottom: 1px solid #E7E7E7;
					font-size: 16px;
					font-weight: bold;
				}
			}

			.rbc-section {
				flex: 1;
				@trans_speed: 0.2s;
				cursor: pointer;

				.rbc-section-content {
					width: 100%;

					@media screen and (min-width: 600px) {
						display: flex;
						flex-grow: 1;
						flex-wrap: wrap;
					}

					.more {
						margin-top: 85px;
					}

					.rbc-item-container {
						flex: 1;
						position: relative;

						.rbc-item-container-body {

							@icon_height: 38px;

							.rbc-item-type {
								border-radius: @icon_height / 2;
								height: @icon_height;
								line-height: @icon_height;
								vertical-align: middle;
								text-align: center;
								padding: 0 15px 0 10px;
								display: inline-block;

								&.rbc-item-type-required {
									background-color: #d4e4ff;
									color: #0d5cdf;
									font-size: 0;
									white-space: nowrap;

									span {
										display: inline-block;
										font-size: 14px;
										height: @icon_height;
										line-height: @icon_height;
										vertical-align: middle;

										&.rbc-item-type-icon {
											font-family: 'Material Icons';
											font-size: 22px;
											margin-right: 5px;
											@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
												/* Support for IE */
												text-rendering: optimizeLegibility;
												font-feature-settings: 'liga';
												ms-font-feature-settings:'liga'
											}
										}
									}
								}
							}

							.rbc-item, .rbc-item-hover {
								box-sizing: border-box;
								padding: 20px;
								background-color: #fff;
								opacity: 1;
								transition: opacity @trans_speed linear;

								.rbc-item-internal {

									.rbc-item-cover {
										border-bottom: 1px solid #E7E7E7;
										padding-bottom: 15px;
										margin-bottom: 12px;

										> div {
											width: 90%;
											height: 170px;
											margin: 0 auto;
										}
									}

									.rbc-item-info {
										margin-bottom: 15px;
									}
								}
							}

							.rbc-item-hover {
								opacity: 0;
								left: 0;
								right: 100%;
								transition: left @trans_speed linear, right @trans_speed linear;
								position: absolute;
								top: 0;
								width: 100%;
								overflow: hidden;
								max-height: 100%;
								height: 100px;

								.rbc-item-info {
									width: 180px;
								}

								.rbc-item-details {
									width: 180px;
								}

								.rbc-tem-info-aside {
									width: 0;
									transition: width @trans_speed linear;
								}
							}

							&:hover,
							&.ada-hover {
								.rbc-item {
									opacity: 0;
								}

								.rbc-item-hover {
									width: auto;
									opacity: 1;
									display: block;
									z-index: 1;
									transition: left @trans_speed linear, right @trans_speed linear;

									box-shadow: 0px 0px 6px 3px #bdbdbd;
									left: 0;
									right: 0;
									max-height: none;
									height: auto;

									@media screen and (min-width: 600px) {
										right: -160px;
									}

									.rbc-item-internal {
										flex: 1;

										.rbc-item-main {
											display: flex;
											padding-bottom: 10px;
											margin-bottom: 20px;
											border-bottom: 1px solid #E7E7E7;
											flex-direction: column;
											@media screen and (min-width: 600px) {
												flex-direction: row;
											}

											.rbc-item-cover {
												flex: 1;
												border: none;
												margin-bottom: 0;
												padding-bottom: 0;
											}

											.rbc-tem-info-aside {
												width: 180px;
												margin-left: 20px;
												padding-top: 10px;
											}
										}
									}

									.rbc-item-internal-additional {
										background-color: #fff;

										.rbc-item-add-line {
											display: flex;
											flex-grow: 1;
											align-items: center;
											margin-bottom: 10px;
											white-space: nowrap;

											flex-direction: column;
											@media screen and (min-width: 600px) {
												flex-direction: row;
											}

											.rbc-item-add-price {
												@media screen and (max-width: 600px) {
													text-align: center;
												}
												flex: 1;
												text-transform: capitalize;

												strong {
													color: @text_highlight_color;
												}
												.product-disclaimer-text {
													margin-left: -35px !important;
												}
											}

											.rbc-item-add-btn {
												@media screen and (max-width: 600px) {
													align-self: stretch;
													margin-top: 5px;
												}
												flex: 1;

												a {
													display: block;
													background-color: #2170f2;
													color: #fff !important;
													text-align: center;
													height: 35px;
													line-height: 35px;
												}
											}
										}
									}
								}
							}
						}

						&:last-of-type {
							.rbc-item-container-body:hover,
							.rbc-item-container-body.ada-hover {

								.rbc-item-hover {
									left: 0 !important;
									right: 0 !important;
								}

								@media screen and (min-width: 600px) {
									.rbc-item-hover {
										left: -160px !important;
										right: 0 !important;
									}
								}
							}
						}
					}
				}
			}
		}

		.loader-container {
			@loading_color: #3BA2F4;
			@width: 80px;

			.loader {
				position: relative;
				margin: 0 auto;
				width: @width;

				&:before {
					content: '';
					display: block;
					padding-top: 100%;
				}
			}

			.circular {
				animation: rotate 2s linear infinite;
				height: 100%;
				transform-origin: center center;
				width: 100%;
				position: absolute;
				top: 0;
				bottom: 0;
				left: 0;
				right: 0;
				margin: auto;
			}

			.path {
				stroke: @loading_color;
				stroke-dasharray: 1, 200;
				stroke-dashoffset: 0;
				animation: dash 1.5s ease-in-out infinite;
				stroke-linecap: round;
			}

			@keyframes rotate {
				100% {
					transform: rotate(360deg);
				}
			}

			@keyframes dash {
				0% {
					stroke-dasharray: 1, 200;
					stroke-dashoffset: 0;
				}
				50% {
					stroke-dasharray: 89, 200;
					stroke-dashoffset: -35px;
				}
				100% {
					stroke-dasharray: 89, 200;
					stroke-dashoffset: -124px;
				}
			}
		}


	}

	// ADA
	@media screen and (max-width: 599px) {
		.rb-container {
			.rbc-other-materials {
				.rbc-section {
					.rbc-section-content {
						.rbc-item-container {
							.rbc-item-container-body {
								.rbc-item-hover {
									.rbc-item-internal-additional {
										display: none;
									}
								}

								&:hover,
								&.ada-hover {
									.rbc-item {
										display: none;
									}

									.rbc-item-hover {
										position: relative;

										.rbc-item-internal-additional {
											display: block;
										}
									}
								}
							}
						}
					}
				}
			}
		}
	}

	@media screen and (max-width: 959px) {
		.rb-container {
			.rbc-other-materials {
				.rbc-section {
					.rbc-section-content {
						.more {
							display: none;
						}
					}
				}
			}
		}
	}
</style>
